/**
 * In-memory API response cache.
 * Repeat reads are served from cache so the browser does not re-fetch the same JSON.
 */

const DEFAULT_TTL_MS = 60_000;

interface CacheEntry {
  data: unknown;
  fetchedAt: number;
}

const store = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

export function buildCacheKey(parts: (string | number | boolean | null | undefined)[]): string {
  return parts
    .filter((part) => part !== undefined && part !== null && part !== '')
    .map(String)
    .join(':');
}

export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> {
  const now = Date.now();
  const hit = store.get(key);
  if (hit && now - hit.fetchedAt < ttlMs) {
    return hit.data as T;
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const promise = fetcher()
    .then((data) => {
      store.set(key, { data, fetchedAt: Date.now() });
      inflight.delete(key);
      return data;
    })
    .catch((error) => {
      inflight.delete(key);
      throw error;
    });

  inflight.set(key, promise);
  return promise;
}

export function invalidateCacheKey(key: string): void {
  store.delete(key);
  inflight.delete(key);
}

export function invalidateCachePrefix(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) {
      store.delete(key);
    }
  }
  for (const key of inflight.keys()) {
    if (key.startsWith(prefix)) {
      inflight.delete(key);
    }
  }
}

export function clearApiCache(): void {
  store.clear();
  inflight.clear();
}
